import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { AutomataService, DataType, RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { DeclinedReason } from '@vendasta/sales-orders';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomationVariableMenuComponent } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.component';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { TRIGGER_STEP_ID } from '../../../../../common/constants';
import { SalesOrdersService } from '../../../../../data-services/sales-orders.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import {
  extractWorkflowStepIdFromReplacementTag,
  isReferencingAPreviousStep,
} from '../../../common/utils/step-reference';

@Component({
  selector: 'automata-decline-sales-order',
  templateUrl: './decline-sales-order-panel.component.html',
  styleUrls: ['./decline-sales-order-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    AutomationVariableMenuComponent,
  ],
})
export class DeclineSalesOrderPanelComponent extends CommonActionPanelComponent implements OnInit {
  usePreviousStepControl: UntypedFormControl = new UntypedFormControl(false, Validators.required);
  previousStepOptions$: Observable<{ title: string; value: string }[]>;
  previousStepOptionsControl = new UntypedFormControl('', Validators.required);
  // this populates options in both the action side panel and the text area Variables menu
  precedingStepIds: string[];

  declinedReasonOptions$: Observable<DeclinedReason[]>;

  declinedReasonFreeTextControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  declinedReasonIdControl: UntypedFormControl = new UntypedFormControl('');
  form: UntypedFormGroup;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly salesOrderService: SalesOrdersService,
    protected readonly automationService: AutomationViewService,
    private readonly automataService: AutomataService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    // the data for this step contains declined_reason_ids (string[]) and declined_reason_message (string)
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    if (isReferencingAPreviousStep(jsonData.declined_reason_ids)) {
      // the declined reason id referencing a previous step means that the entire reason is being parameter passed
      this.usePreviousStepControl.setValue(true);
    } else {
      this.declinedReasonFreeTextControl.setValue(jsonData.declined_reason_message || '');
    }
    this.declinedReasonOptions$ = this.automationService.isNonPartnerAutomation().pipe(
      switchMap((isTemplateView) =>
        isTemplateView ? of([]) : this.salesOrderService.getAllDeclinedReasons(this.automation.namespace),
      ),
      take(1),
      map((declinedReasons) => declinedReasons.filter((dr) => !dr.archived)),
      tap((declinedReasons) => {
        let selectedDeclinedReasonId = '';
        if (jsonData.declined_reason_ids?.length > 0 && !isReferencingAPreviousStep(jsonData.declined_reason_ids[0])) {
          selectedDeclinedReasonId = jsonData.declined_reason_ids[0];
        }
        if (declinedReasons.map((dr) => dr.id).indexOf(selectedDeclinedReasonId) !== -1) {
          this.declinedReasonIdControl.setValue(selectedDeclinedReasonId);
        }
        if (declinedReasons.length > 0) {
          this.declinedReasonIdControl.addValidators(Validators.required);
        }
        this.savingEnabled = true;
      }),
    );
    this.form = this.fb.group({
      declinedReason: this.declinedReasonFreeTextControl,
      declineReasonId: this.declinedReasonIdControl,
    });

    if (!!this.precedingSteps && this.precedingSteps.length > 0) {
      this.precedingStepIds = this.precedingSteps;
    } else {
      // trigger is not included in the list of workflow step ids
      this.precedingStepIds = [TRIGGER_STEP_ID];
    }

    this.previousStepOptions$ = this.automataService
      .getWorkflowStepPredecessorOutputParams(this.automation.id, this.precedingStepIds, true)
      .pipe(
        map((resp) => {
          if (!resp) {
            return [];
          }
          const currentlySavedData = this.step ? JSON.parse(this.step.data) : {};
          const savedPrecedingStepId = extractWorkflowStepIdFromReplacementTag(currentlySavedData.declined_reason_ids);

          const outputParamOptions = [];
          for (const r of resp) {
            // filter for output params that have the proper data type
            const supportedOutputParameters = r.outputParameters?.filter((outputParam) => {
              return outputParam.settings.dataType === DataType.DATA_TYPE_ORDER_DECLINED_REASON_IDS;
            });
            if (!!supportedOutputParameters && supportedOutputParameters.length > 0) {
              const workflowStepKeyPrefix = '.WorkflowStep.' + r.workflowStep.id + '.';
              const selected = savedPrecedingStepId ? r.workflowStep.id === savedPrecedingStepId : false;
              if (selected) {
                this.previousStepOptionsControl.setValue('.WorkflowStep.' + r.workflowStep.id + '.');
              }
              outputParamOptions.push({
                title: r.workflowStep.name,
                value: workflowStepKeyPrefix,
              });
            }
          }
          this.savingEnabled = true;
          return outputParamOptions;
        }),
      );
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }
    if (this.usePreviousStepControl.value) {
      // if "use data from previous step" chosen, set both values to reference the previous step key
      jsonData.declined_reason_message = '{' + this.previousStepOptionsControl.value + 'declined_reason_message}';
      // this will evaluate into a list of strings at the step's execution time, when the value is extracted from the previous step
      jsonData.declined_reason_ids = '{' + this.previousStepOptionsControl.value + 'declined_reason_ids}';
      return jsonData;
    }
    jsonData.declined_reason_message = this.declinedReasonFreeTextControl.value;
    jsonData.declined_reason_ids = [this.declinedReasonIdControl.value];
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    if (this.usePreviousStepControl.value) {
      return [this.previousStepOptionsControl];
    }
    return [this.form];
  }

  isFormValid(): boolean {
    if (this.usePreviousStepControl.value) {
      return this.previousStepOptionsControl.valid;
    }
    return this.form.valid;
  }

  insertVariable(elementID: string, control: AbstractControl, variable: string): void {
    insertVariable(elementID, control, variable);
  }
}
